<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <div v-if="year" id="container" :class="{hasHeaderRight: Boolean($slots.headerRight)}">
    <!-- headerRight: e.g. /2026's time zone menu, on the heading's row, over the right column -->
    <div class="header">
      <h3>Key Dates<sup v-if="!footnotes.length" id="footnote">*</sup></h3>
      <div v-if="$slots.headerRight" class="headerRight"><slot name="headerRight" /></div>
    </div>
    <!-- https://stackoverflow.com/q/45926982 -->
    <template v-for="(item, key) in items" :key="item">
      <div :class="[past_or_future(item)]">
        <b v-if="item.daysBeforeOld">
          <span class="dataOld">{{ date(item.daysBeforeOld) }}:</span>
          {{ date(item.daysBefore) }}:
        </b>
        <b v-else-if="item.datesOld">
          <span class="dataOld">{{ shorten(item.datesOld) }}:</span>
          {{ item.dates ? shorten(item.dates) + ":" : "" }}
        </b>
        <b v-else-if="item.daysBefore">{{ date(item.daysBefore) }}:</b>
        <b v-else-if="item.dates"
        >{{ shorten(item.dates) }}<sup v-if="item.footnote">*</sup>:</b
        >
      </div>
      <div :class="[past_or_future(item)]">
        <span v-if="!key.match(/VIZBI|Masterclass/)">Deadline for </span>
        <!-- to='somewhere' redirect to '/year/somewhere' -->
        <a v-if="item?.link.startsWith('http')" :href="item.link">
          <template v-for="(part, index) in getLabelParts(item.label || key)" :key="index">
            <i v-if="index">Online</i>{{ part }}
          </template>
        </a>
        <router-link v-else :to="`${item.link}`">
          <template v-for="(part, index) in getLabelParts(item.label || key)" :key="index">
            <i v-if="index">Online</i>{{ part }}
          </template>
        </router-link>
        <!-- span v-if="showCounter(key, item)"> ({{ counter.value }})</span -->
        <!-- "(EXTENDED)" off (Seán, 2026-10-29): the struck-out old date says it alone
        <span v-if="item.daysBeforeOld || item.datesOld" class="extended"> (EXTENDED)</span> -->
      </div>
    </template>
    <!-- an entry's own footnote (its exact time in the viewer's zone: useLocalEventDates.js) -->
    <p v-for="footnote in footnotes" :key="footnote">
      <span>*</span>
      <span>{{ footnote }}</span>
    </p>
    <p v-if="!footnotes.length">
      <span>*</span>
      <span>
        Each deadline ends at 11:59 PM Pacific Time (San Francisco), regardless of your local time
        zone.
      </span>
    </p>
  </div>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(); // eslint-disable-line
import {ref, computed} from "vue";
import {useRoute} from "vue-router";
import {DateTime} from "luxon"; // https://tinyurl.com/luxon-global
import time_zone from "@/assets/history";

const props = defineProps(["items"]);
const footnotes = computed(() => {
  return Object.values(props.items || {}).map((item) => item.footnote).filter(Boolean);
});
const ONLINE_WORD = /\bOnline\b/; // "VIZBI Online Workshop" → VIZBI <i>Online</i> Workshop

const route = useRoute();
const counter = ref([]);
const counterAlreadyShown = ref(false);
const year = ref(Number(route.params.year));

const startDate = computed(() => {
  // find first item beginning with 'VIZBI' with a date
  if (!props?.items) return;
  let vizbi = Object.keys(props.items).find((key) => key.match(/^VIZBI/) && props.items[key].dates);
  if (!vizbi) return;
  let matches = props.items[vizbi].dates.match(/^(\w+)\s*,*(\d+)/);
  let [monthName, day] = [matches[1], matches[2]];
  let month = new Date(Date.parse(monthName + " 1, 2012")).getMonth() + 1;
  return {year: year.value, month: month, day: day, hour: 9, minute: 0};
});

const startDateInMeetingTimeZone = computed(() => {
  return DateTime.fromObject(startDate.value, {zone: time_zone[year.value]});
});

const startDateInLA = computed(() => {
  let closingDate = structuredClone(startDate.value);
  closingDate.hour = 23;
  closingDate.minute = 59;
  return DateTime.fromObject(closingDate, {zone: "America/Los_Angeles"});
});

// the label split around each "Online", which is shown in italics; an item's own label (e.g.
// "early-bird registration") wins over its name in keyDates.json
const getLabelParts = (key) => key.split(ONLINE_WORD);

const date = (daysBefore) => {
  return startDateInMeetingTimeZone.value.plus({days: daysBefore}).toFormat("MMM d");
};

const shorten = (dateString, format = "MMM d") => {
  const dateRangeMatch = dateString.match(/^(\w+)\s+(\d+)(?:-(\d+))?$/);
  if (!dateRangeMatch) {
    const parsed = DateTime.fromFormat(dateString, "MMMM d");
    if (!parsed.isValid) return dateString; // month-only, e.g. "November"
    return parsed.toFormat(format);
  }
  const [, month, startDay, endDay] = dateRangeMatch;
  const shortMonth = DateTime.fromFormat(month, "MMMM").toFormat("MMM");
  return endDay ? `${shortMonth} ${startDay}–${endDay}` : `${shortMonth} ${startDay}`;
};

const past_or_future = (item) => {
  if (item.daysBefore) {
    log(`KeyDates: past_or_future: daysBefore = ${item.daysBefore}`);
    let dueAt = startDateInLA.value.plus({days: item.daysBefore});
    log(`KeyDates: past_or_future: dueAt = ${dueAt}`);
    if (!dueAt.isValid) {
      console.error(`Invalid date: ${dueAt}`);
      return "invalid_date";
    }
    if (dueAt.endOf("day") >= DateTime.now()) {
      if (!counterAlreadyShown.value) {
        counter.value = DateTime.now().diff(dueAt, ["days", "hours", "minutes", "seconds"]);
      }
      return "future";
    } else {
      return "past";
    }
  }
  if (item.dates) {
    log(`KeyDates: past_or_future: dates = ${item.dates}`);
    let endDate = item.dates.match(/^(\w+)\s+(\d+)(?:-(\d+))?$/);
    if (!endDate) {
      return "future";
    }
    let year = startDateInLA.value.toFormat("yyyy");
    let endDay = endDate[3] || endDate[2]; // multi-day || one-day event
    let endDateString = `${endDate[1]} ${endDay}, ${year}`;
    log(`KeyDates: past_or_future: ends ${endDateString}`);
    endDate = DateTime.fromFormat(endDateString, "MMMM d, yyyy", {zone: "America/Los_Angeles"}).set(
      {hour: 23, minute: 59}
    );

    log(`KeyDates: past_or_future: endDate = ${endDate}`);
    if (!endDate.isValid) {
      console.error(`Gives invalid date: ${endDate}`);
      return "invalid_date";
    }
    if (endDate.endOf("day") >= DateTime.now()) {
      if (!counterAlreadyShown.value) {
        counter.value = DateTime.now().diff(endDate, ["days", "hours", "minutes", "seconds"]);
      }
      return "future";
    } else {
      return "past";
    }
  }
};
</script>

<style scoped>
/* https://css-tricks.com/snippets/css/complete-guide-grid/ */
div#container {
  display: grid;
  grid-template-columns: minmax(10px, 1fr) 3fr;
  grid-template-rows: auto;
  grid-row-gap: 0.3em;
}
:is(.header, p) {
  grid-column: 1 / -1; /* spans from column 1 to column -1 (last column) */
}
/* the heading, and anything in headerRight beside it on the same row (Seán, 2026-10-22: at the
   row's far end, /2026's time zone menu was far from the dates and close to the sidebar) */
.header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 1rem;
}
h3 {
  margin: 0; /* remove default margin */
  margin-top: 1em;
}
/* with something beside the heading (Seán, 2026-10-29): the heading's row on the grid's columns,
   the control over the right column, level with the heading's letters (as tall, the same top and
   bottom), and a little space under the row, so it doesn't read as a table's first row; the left
   column as wide as the heading or the longest date. On a phone, the control under the heading */
#container.hasHeaderRight {
  grid-template-columns: max-content 1fr;
  column-gap: 1.5em;
}
.hasHeaderRight .header {
  display: grid;
  grid-template-columns: subgrid;
  column-gap: inherit; /* the grid's, not the flex row's 1rem: the control over the text */
  align-items: baseline; /* the control's text on the heading's baseline */
  margin-bottom: 0.5em;
}
.headerRight {
  min-width: 0;
  position: relative;
  top: 2.5px; /* the button's baseline is its globe's bottom, its text's 2.5px lower */
}
/* from a little above the heading's capitals to its baseline (Seán, 2026-10-29) */
.header .headerRight :deep(.trigger) {
  line-height: 1.25;
  padding-block: 2.5px;
}
.narrow .hasHeaderRight .header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 1rem;
  row-gap: 0.4em;
}

p {
  display: flex;
  font-size: 0.85rem;
  color: #555;
  margin-top: 1rem;
  line-height: 1.2;
}

p span:first-child {
  flex: none;
  margin-right: 0.5rem;
}

b {
  font-weight: 600; /* not quite so bold - 300 for light text */
}
.past {
  text-decoration: line-through;
}
.past,
.past a {
  color: silver; /* switch color */
}
.invalid_date,
.invalid_date a {
  color: red;
}
/* the old date and "(EXTENDED)" alike, light and grey (Seán, 2026-10-29): the note is an aside,
   small, not struck out */
.dataOld,
.extended {
  color: rgb(143, 143, 143);
  font-weight: 300;
}
.dataOld {
  text-decoration: line-through;
}
.extended {
  font-size: 0.7em;
}
</style>
